import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader2, Printer, Download, Mail, MapPin, Calendar, CreditCard, Tag, ShoppingBag, User } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { useCart } from '../App';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isPreview = searchParams.get('preview') === 'true'; // For testing receipt design
  const { clearCart } = useCart();
  const [status, setStatus] = useState(isPreview ? 'success' : 'loading');
  const [orderData, setOrderData] = useState(isPreview ? {
    id: 'PREVIEW-ORDER-12345',
    customer_name: 'John Smith',
    customer_email: 'john.smith@example.com',
    shipping_address: '123 Main St, Toronto, ON M5V 1A1, Canada',
    currency: 'CAD',
    payment_method: 'credit_card',
    created_at: new Date().toISOString(),
    items: [
      { name: 'Homelyph Medium', price: 5349, quantity: 1, image_url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800' }
    ],
    subtotal: 5349,
    discount_amount: 550,
    discount_source: 'sale',
    credit_card_surcharge: 139.17,
    total: 4938.17
  } : null);
  const [cartItems, setCartItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    // Retrieve stored checkout data
    const storedCart = sessionStorage.getItem('checkout_cart_items');
    const storedCustomer = sessionStorage.getItem('checkout_customer_info');
    const storedDiscount = sessionStorage.getItem('checkout_discount_info');
    
    if (storedCart) setCartItems(JSON.parse(storedCart));
    if (storedCustomer) setCustomerInfo(JSON.parse(storedCustomer));
    if (storedDiscount) setDiscountInfo(JSON.parse(storedDiscount));
  }, []);

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 30; // Extended for new window flow
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/api/orders/status/${sessionId}`);
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setOrderData(res.data.order);
          // Payment confirmed — NOW it's safe to clear the cart.
          // (Previously the cart was cleared the moment we redirected to
          // Stripe, which meant customers who hit Back lost their items.)
          clearCart();
          // Clean up sessionStorage after successful payment
          sessionStorage.removeItem('checkout_cart_items');
          sessionStorage.removeItem('checkout_customer_info');
          sessionStorage.removeItem('checkout_discount_info');
          sessionStorage.removeItem('checkout_session_id');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (e) {
        console.error('Error checking status:', e);
        // Don't immediately error out, keep polling
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    // Skip polling in preview mode
    if (isPreview) {
      return;
    }
    
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, isPreview]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate totals from order data or cart items
  const calculateTotals = () => {
    // Prefer line_items_calculated which has the correct currency-specific prices
    const items = orderData?.line_items_calculated || orderData?.items || cartItems;
    
    // Subtotal from order (already calculated with correct currency) or calculate from items
    const subtotal = orderData?.subtotal || orderData?.msrp_total || items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const currency = orderData?.currency || discountInfo?.currency || 'CAD';
    
    let discount = orderData?.discount_amount || 0;
    let discountLabel = '';
    
    // Determine discount label based on source
    if (orderData?.discount_source === 'affiliate' || orderData?.affiliate_code) {
      discountLabel = orderData?.affiliate_code ? `Affiliate: ${orderData.affiliate_code}` : 'Affiliate Discount';
    } else if (orderData?.discount_source === 'coupon' || orderData?.coupon_code) {
      discountLabel = orderData?.coupon_code ? `Coupon: ${orderData.coupon_code}` : 'Coupon Discount';
    } else if (orderData?.discount_source === 'sale' || discount > 0) {
      discountLabel = 'Sale Discount';
    } else if (discountInfo?.activeDiscount) {
      discount = discountInfo.activeDiscount.amount || 0;
      discountLabel = discountInfo.appliedAffiliate?.affiliate_name 
        ? `Affiliate: ${discountInfo.appliedAffiliate.affiliate_name}`
        : discountInfo.appliedCoupon?.code 
          ? `Coupon: ${discountInfo.appliedCoupon.code}`
          : 'Discount';
    }
    
    // Use total from order data, or calculate from subtotal
    const amountPaid = orderData?.total || (subtotal - discount);
    const ccFee = orderData?.credit_card_surcharge || 0;
    
    return { subtotal, discount, discountLabel, amountPaid, currency, ccFee };
  };

  const { subtotal, discount, discountLabel, amountPaid, currency, ccFee } = calculateTotals();
  // Use line_items_calculated for correct currency-specific prices
  const displayItems = orderData?.line_items_calculated || orderData?.items || cartItems;
  const displayCustomer = orderData ? {
    name: orderData.customer_name,
    email: orderData.customer_email,
    address: orderData.shipping_address
  } : customerInfo;

  return (
    <div className="min-h-screen bg-[#f0f4f8] py-8 px-4 md:px-6">
      {/* Print-specific styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .receipt-container { 
            box-shadow: none !important; 
            max-width: 100% !important;
            padding: 20px !important;
          }
        }
        .print-only { display: none; }
      `}</style>

      <div className="max-w-3xl mx-auto">
        {/* Logo - no print */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8 no-print">
          <Logo size="lg" />
          <span className="text-3xl font-bold text-[#0a0203]">HomeLyph</span>
        </Link>

        {/* Main Receipt Card */}
        <div ref={receiptRef} className="receipt-container bg-white rounded-3xl p-6 md:p-10 shadow-xl">
          
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#dbeef5] rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-[#003456] animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-[#0a0203] mb-4">
                Waiting for Payment...
              </h1>
              <p className="text-gray-600 mb-2">
                Complete your payment in the Stripe window.
              </p>
              <p className="text-sm text-gray-500">
                This page will automatically update once payment is confirmed.
              </p>
            </div>
          )}

          {/* Success State - Full Receipt */}
          {status === 'success' && (
            <>
              {/* Header */}
              <div className="text-center mb-8 border-b border-gray-200 pb-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-12 w-12 text-emerald-600" />
                </div>
                <h1 className="text-3xl font-bold text-[#0a0203] mb-2">Order Confirmed!</h1>
                <p className="text-gray-600">Thank you for your purchase</p>
                
                {/* Print Header - only visible when printing */}
                <div className="print-only mt-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Logo size="md" />
                    <span className="text-xl font-bold">HomeLyph</span>
                  </div>
                  <p className="text-sm text-gray-500">Order Receipt</p>
                </div>
              </div>

              {/* Order Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Order Details */}
                <div className="bg-[#f8fafc] rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="h-5 w-5 text-[#003456]" />
                    <span className="font-semibold text-[#0a0203]">Order Details</span>
                  </div>
                  <div className="space-y-3 text-sm">
                    {orderData?.id && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Order ID</span>
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{orderData.id.slice(-8).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date</span>
                      <span>{formatDate(orderData?.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status</span>
                      <span className="text-emerald-600 font-semibold flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" /> Paid
                      </span>
                    </div>
                    {orderData?.payment_method && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Method</span>
                        <span className="capitalize">{orderData.payment_method.replace('_', ' ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="bg-[#f8fafc] rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <User className="h-5 w-5 text-[#003456]" />
                    <span className="font-semibold text-[#0a0203]">Customer Information</span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-gray-400 mt-0.5" />
                      <span>{displayCustomer?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                      <span>{displayCustomer?.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <span className="text-gray-700">{displayCustomer?.address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Purchased */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag className="h-5 w-5 text-[#003456]" />
                  <span className="font-semibold text-[#0a0203]">Items Purchased</span>
                </div>
                <div className="bg-[#f8fafc] rounded-2xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#003456] text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium">Item</th>
                        <th className="text-center py-3 px-4 font-medium">Qty</th>
                        <th className="text-right py-3 px-4 font-medium">Price ({currency})</th>
                        <th className="text-right py-3 px-4 font-medium">Total ({currency})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayItems.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200 last:border-0">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              {item.image_url && (
                                <img 
                                  src={item.image_url} 
                                  alt={item.name}
                                  className="w-12 h-12 rounded-lg object-cover no-print"
                                />
                              )}
                              <div>
                                <p className="font-medium text-[#0a0203]">{item.name}</p>
                                {item.configuration && (
                                  <p className="text-xs text-gray-500">
                                    {Object.entries(item.configuration).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                  </p>
                                )}
                                {item.discount_applied && (
                                  <p className="text-xs text-emerald-600">
                                    {item.discount_applied === 'sale' ? 'Sale price' : 
                                     item.discount_applied === 'affiliate' ? 'Affiliate discount applied' : 
                                     'Discount applied'}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center text-gray-600">{item.quantity}</td>
                          <td className="py-4 px-4 text-right font-mono text-gray-600">
                            ${item.price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </td>
                          <td className="py-4 px-4 text-right font-mono font-semibold">
                            ${((item.price || 0) * (item.quantity || 1)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-br from-[#003456] to-[#001d30] rounded-2xl p-6 text-white mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5" />
                  <span className="font-semibold">Payment Summary</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-300">
                    <span>Subtotal</span>
                    <span className="font-mono">${subtotal.toLocaleString()} {currency}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-emerald-300">
                      <span className="flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        {discountLabel}
                      </span>
                      <span className="font-mono">-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-300">
                    <span>Shipping</span>
                    <span className="font-mono">
                      {orderData?.shipping_cost ? `$${orderData.shipping_cost.toLocaleString()}` : 'Included'}
                    </span>
                  </div>
                  {ccFee > 0 && (
                    <div className="flex justify-between text-gray-300">
                      <span>Credit Card Fee ({orderData?.credit_card_surcharge_percent || 2.9}%)</span>
                      <span className="font-mono">${ccFee.toFixed(2)}</span>
                    </div>
                  )}
                  {orderData?.store_credit_amount > 0 && (
                    <div className="flex justify-between text-blue-300">
                      <span className="flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        Store Credit ({orderData.store_credit_code})
                      </span>
                      <span className="font-mono">-${orderData.store_credit_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {orderData?.tax && orderData.tax > 0 && (
                    <div className="flex justify-between text-gray-300">
                      <span>Tax</span>
                      <span className="font-mono">${orderData.tax.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-white/20 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total Paid</span>
                      <span className="text-2xl font-bold font-mono">
                        ${amountPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 no-print">
                <Button 
                  onClick={handlePrint}
                  variant="outline"
                  className="flex-1 border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white py-6"
                  data-testid="print-receipt-btn"
                >
                  <Printer className="mr-2 h-5 w-5" />
                  Print Receipt
                </Button>
                <Link to="/products" className="flex-1">
                  <Button 
                    className="w-full bg-[#003456] hover:bg-[#001d30] text-white py-6"
                    data-testid="continue-shopping-btn"
                  >
                    Continue Shopping <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>

              {/* Footer Note */}
              <div className="mt-8 text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
                <p>A confirmation email has been sent to {displayCustomer?.email}</p>
                <p className="mt-1">Questions? Contact us at <a href="mailto:support@homelyph.com" className="text-[#003456] hover:underline">support@homelyph.com</a></p>
              </div>
            </>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#FEE2E2] rounded-full flex items-center justify-center">
                <span className="text-4xl">⏰</span>
              </div>
              <h1 className="text-2xl font-bold text-[#0a0203] mb-4">
                Session Expired
              </h1>
              <p className="text-gray-600 mb-8">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/cart">
                <Button className="bg-[#003456] hover:bg-[#001d30] text-white py-6 px-8">
                  Return to Cart
                </Button>
              </Link>
            </div>
          )}

          {/* Error/Timeout State */}
          {(status === 'error' || status === 'timeout') && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#FEE2E2] rounded-full flex items-center justify-center">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold text-[#0a0203] mb-4">
                {status === 'timeout' ? 'Payment Not Confirmed' : 'Something Went Wrong'}
              </h1>
              <p className="text-gray-600 mb-8">
                {status === 'timeout' 
                  ? 'We couldn\'t confirm your payment in time. If you completed the payment, please contact support.'
                  : 'We couldn\'t verify your payment. Please contact support if you believe this is an error.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="bg-[#003456] hover:bg-[#001d30] text-white py-6 px-8">
                    Contact Support
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white py-6 px-8">
                    Return Home
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Track Order Link */}
        {status === 'success' && orderData?.id && (
          <div className="text-center mt-6 no-print">
            <Link 
              to={`/track-order?order=${orderData.id}`}
              className="text-[#003456] hover:underline text-sm"
            >
              Track your order →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
